import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import {
  PrestampDetailRow,
  PrestampRequestForm,
  PrestampRequestRow,
  PrestampStatus,
  PrestampSyncStatus,
} from '../../../models/parking/prestamp-taiwan.model';

/**
 * Mock store for Pre-discount Taiwan UI until Odoo tables + live API wiring.
 * Replace with ApiPrestampTaiwanService when Platform endpoints are persisted.
 */
@Injectable({ providedIn: 'root' })
export class MockPrestampTaiwanService {
  private nextId = 3;
  private nextLogId = 3;
  private rows: PrestampRequestRow[] = [
    {
      id: 1,
      name: 'PST-2026-00001',
      siteId: 1,
      siteName: 'Demo Site',
      lotId: 'PSS_123',
      ruleUuid: '0043a8e1-a7a1-420d-8f27-4eef3e157a78',
      quantity: 10,
      stampLimit: 100,
      status: 'approved',
      taiwanSyncStatus: 'success',
      taiwanScheduleUuid: 'e349cbb4-a8c3-40f8-afec-2418ae42e137',
      detailCount: 2,
      createDate: '2026-07-01T10:00:00',
      submittedDate: '2026-07-01T10:05:00',
      details: [
        { sequence: 0, licensePlate: 'ABC1234', stampDate: '2025-01-01' },
        { sequence: 1, licensePlate: 'ABC1235', stampDate: '2025-01-02' },
      ],
      logs: [
        {
          id: 1,
          attemptNo: 1,
          triggerType: 'auto',
          status: 'success',
          endpointUrl: '/shop-api/v2/coupons/schedules',
          httpStatusCode: 200,
          sentAt: '2026-07-01T10:05:01',
          completedAt: '2026-07-01T10:05:02',
          durationMs: 420,
        },
      ],
    },
    {
      id: 2,
      name: 'PST-2026-00002',
      siteId: 1,
      siteName: 'Demo Site',
      lotId: 'PSS_123',
      ruleUuid: '0043a8e1-a7a1-420d-8f27-4eef3e157a78',
      quantity: 5,
      stampLimit: 50,
      status: 'draft',
      taiwanSyncStatus: 'not_sent',
      taiwanScheduleUuid: null,
      detailCount: 1,
      createDate: '2026-07-10T09:00:00',
      details: [{ sequence: 0, licensePlate: 'กข1234', stampDate: '2026-08-01' }],
      logs: [],
    },
  ];

  list(params: {
    siteId?: number;
    status?: string;
    syncStatus?: string;
    searchText?: string;
  }): Observable<PrestampRequestRow[]> {
    let data = [...this.rows];
    if (params.siteId && params.siteId > 0) {
      data = data.filter((r) => r.siteId === params.siteId);
    }
    if (params.status && params.status !== 'all') {
      data = data.filter((r) => r.status === params.status);
    }
    if (params.syncStatus && params.syncStatus !== 'all') {
      data = data.filter((r) => r.taiwanSyncStatus === params.syncStatus);
    }
    if (params.searchText?.trim()) {
      const s = params.searchText.trim().toLowerCase();
      data = data.filter(
        (r) =>
          r.name.toLowerCase().includes(s) ||
          (r.taiwanScheduleUuid ?? '').toLowerCase().includes(s) ||
          (r.details ?? []).some((d: PrestampDetailRow) => d.licensePlate.toLowerCase().includes(s))
      );
    }
    return of(data).pipe(delay(200));
  }

  getById(id: number): Observable<PrestampRequestRow | null> {
    const found = this.rows.find((r) => r.id === id) ?? null;
    return of(found ? structuredClone(found) : null).pipe(delay(150));
  }

  create(form: PrestampRequestForm, siteName: string, lotId: string, submit: boolean): Observable<PrestampRequestRow> {
    const row: PrestampRequestRow = {
      id: this.nextId++,
      name: `PST-2026-${String(this.nextId - 1).padStart(5, '0')}`,
      siteId: form.siteId,
      siteName,
      lotId,
      ruleUuid: form.ruleUuid.trim(),
      quantity: form.quantity,
      stampLimit: form.stampLimit,
      status: submit ? 'approved' : 'draft',
      taiwanSyncStatus: submit ? 'success' : 'not_sent',
      taiwanScheduleUuid: submit ? crypto.randomUUID() : null,
      detailCount: form.details.length,
      createDate: new Date().toISOString(),
      submittedDate: submit ? new Date().toISOString() : null,
      requestRemark: form.requestRemark || null,
      details: form.details.map((d: PrestampRequestForm['details'][number], i: number) => ({
        sequence: i,
        licensePlate: d.licensePlate.trim(),
        stampDate: d.stampDate,
      })),
      logs: submit
        ? [
            {
              id: this.nextLogId++,
              attemptNo: 1,
              triggerType: 'auto',
              status: 'success',
              endpointUrl: '/shop-api/v2/coupons/schedules',
              httpStatusCode: 200,
              sentAt: new Date().toISOString(),
              completedAt: new Date().toISOString(),
              durationMs: 300,
            },
          ]
        : [],
    };
    this.rows = [row, ...this.rows];
    return of(structuredClone(row)).pipe(delay(250));
  }

  updateDraft(id: number, form: PrestampRequestForm): Observable<boolean> {
    const row = this.rows.find((r) => r.id === id);
    if (!row || row.status !== 'draft') return of(false);
    row.ruleUuid = form.ruleUuid.trim();
    row.quantity = form.quantity;
    row.stampLimit = form.stampLimit;
    row.requestRemark = form.requestRemark || null;
    row.details = form.details.map((d: PrestampRequestForm['details'][number], i: number) => ({
      sequence: i,
      licensePlate: d.licensePlate.trim(),
      stampDate: d.stampDate,
    }));
    row.detailCount = row.details.length;
    return of(true).pipe(delay(200));
  }

  updateApprovedPairs(id: number, form: PrestampRequestForm): Observable<boolean> {
    const row = this.rows.find((r) => r.id === id);
    if (!row || row.status !== 'approved' || !row.taiwanScheduleUuid) return of(false);
    row.stampLimit = form.stampLimit;
    row.details = form.details.map((d: PrestampRequestForm['details'][number], i: number) => ({
      sequence: i,
      licensePlate: d.licensePlate.trim(),
      stampDate: d.stampDate,
    }));
    row.detailCount = row.details.length;
    row.taiwanSyncStatus = 'success';
    row.logs = [
      {
        id: this.nextLogId++,
        attemptNo: (row.logs?.length ?? 0) + 1,
        triggerType: 'manual',
        status: 'success',
        endpointUrl: `/shop-api/v2/coupons/schedules/${row.taiwanScheduleUuid}`,
        httpStatusCode: 200,
        sentAt: new Date().toISOString(),
        completedAt: new Date().toISOString(),
        durationMs: 280,
      },
      ...(row.logs ?? []),
    ];
    return of(true).pipe(delay(250));
  }

  submit(id: number): Observable<boolean> {
    const row = this.rows.find((r) => r.id === id);
    if (!row || row.status !== 'draft') return of(false);
    row.status = 'approved';
    row.submittedDate = new Date().toISOString();
    row.taiwanSyncStatus = 'success';
    row.taiwanScheduleUuid = crypto.randomUUID();
    row.logs = [
      {
        id: this.nextLogId++,
        attemptNo: 1,
        triggerType: 'auto',
        status: 'success',
        endpointUrl: '/shop-api/v2/coupons/schedules',
        httpStatusCode: 200,
        sentAt: new Date().toISOString(),
        completedAt: new Date().toISOString(),
        durationMs: 310,
      },
    ];
    return of(true).pipe(delay(250));
  }

  cancel(id: number): Observable<boolean> {
    const row = this.rows.find((r) => r.id === id);
    if (!row || row.status === 'cancelled' || row.status === 'rejected') return of(false);
    row.status = 'cancelled';
    if (row.taiwanScheduleUuid) {
      row.logs = [
        {
          id: this.nextLogId++,
          attemptNo: (row.logs?.length ?? 0) + 1,
          triggerType: 'manual',
          status: 'success',
          endpointUrl: `/shop-api/v2/coupons/schedules/${row.taiwanScheduleUuid}`,
          httpStatusCode: 200,
          sentAt: new Date().toISOString(),
          completedAt: new Date().toISOString(),
          durationMs: 200,
        },
        ...(row.logs ?? []),
      ];
    }
    return of(true).pipe(delay(200));
  }

  retry(id: number): Observable<boolean> {
    const row = this.rows.find((r) => r.id === id);
    if (!row || row.status !== 'approved' || row.taiwanScheduleUuid) return of(false);
    row.taiwanSyncStatus = 'success' as PrestampSyncStatus;
    row.taiwanScheduleUuid = crypto.randomUUID();
    row.logs = [
      {
        id: this.nextLogId++,
        attemptNo: (row.logs?.length ?? 0) + 1,
        triggerType: 'manual',
        status: 'success',
        endpointUrl: '/shop-api/v2/coupons/schedules',
        httpStatusCode: 200,
        sentAt: new Date().toISOString(),
        completedAt: new Date().toISOString(),
        durationMs: 350,
      },
      ...(row.logs ?? []),
    ];
    return of(true).pipe(delay(250));
  }

  statusLabel(status: PrestampStatus): string {
    switch (status) {
      case 'draft':
        return 'ร่าง';
      case 'pending_approval':
        return 'รออนุมัติ';
      case 'approved':
        return 'อนุมัติแล้ว';
      case 'rejected':
        return 'ปฏิเสธ';
      case 'cancelled':
        return 'ยกเลิก';
      default:
        return status;
    }
  }

  syncLabel(status: PrestampSyncStatus): string {
    switch (status) {
      case 'not_sent':
        return 'ยังไม่ส่ง';
      case 'pending':
        return 'กำลังส่ง';
      case 'success':
        return 'สำเร็จ';
      case 'failed':
        return 'ล้มเหลว';
      default:
        return status;
    }
  }
}
